import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export type EmergencyPrintType =
  | 'contract'
  | 'promissory'
  | 'inventory'
  | 'handover'
  | 'bundle';

const PRINT_OPTIONS: {
  key: EmergencyPrintType;
  label: string;
  icon: string;
}[] = [
  { key: 'contract', label: '空白合約', icon: 'ri-file-text-line' },
  { key: 'promissory', label: '空白本票', icon: 'ri-bank-card-line' },
  { key: 'inventory', label: '空白器材清單', icon: 'ri-list-check' },
  { key: 'handover', label: '空白交接單', icon: 'ri-hand-coin-line' },
  { key: 'bundle', label: '空白合約包', icon: 'ri-stack-line' },
];

const TYPE_LABEL_MAP: Record<EmergencyPrintType, string> = {
  contract: '空白合約',
  promissory: '空白本票',
  inventory: '空白器材清單',
  handover: '空白交接單',
  bundle: '空白合約包',
};

interface Props {
  onSelectType?: (type: EmergencyPrintType) => void;
}

export default function EmergencyPrintDropdown({ onSelectType }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [selectedType, setSelectedType] = useState<EmergencyPrintType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (type: EmergencyPrintType) => {
    setSelectedType(type);
    setOpen(false);
    setShowWarning(true);
    onSelectType?.(type);
  };

  const handleConfirm = () => {
    if (!selectedType) return;
    setShowWarning(false);
    navigate(`/admin/orders/emergency-print/${selectedType}`);
  };

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-printer-line text-base"></i>
          緊急列印
          <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? 'rotate-180' : ''}`}></i>
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-100">
              <div className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>
                紙本備援列印
              </div>
            </div>
            {PRINT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => handleSelect(opt.key)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <i className={`${opt.icon} text-gray-600`}></i>
                </div>
                <span className="font-medium">{opt.label}</span>
              </button>
            ))}
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
              <div className="text-[10px] text-gray-400 leading-relaxed">
                不依賴系統合約預覽，紙本簽署後應回填系統。
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl p-6 max-w-lg mx-4 shadow-2xl w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-alarm-warning-line text-2xl text-amber-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">緊急紙本備援列印</h3>
                <p className="text-sm text-gray-500">
                  {selectedType ? TYPE_LABEL_MAP[selectedType] : ''}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <i className="ri-shield-check-line text-amber-500 mt-0.5 shrink-0"></i>
                <span>請現場核對承租人身分證件</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <i className="ri-camera-line text-amber-500 mt-0.5 shrink-0"></i>
                <span>請影印或掃描身分證件，依公司資料保存規範處理</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <i className="ri-file-list-3-line text-amber-500 mt-0.5 shrink-0"></i>
                <span>請確認合約、本票、器材清單、交接單是否完整</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <i className="ri-upload-cloud-2-line text-amber-500 mt-0.5 shrink-0"></i>
                <span>紙本簽署後應回填系統或上傳掃描檔</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <i className="ri-barcode-line text-amber-500 mt-0.5 shrink-0"></i>
                <span>空白合約列印字號未來需與紙本文件一致</span>
              </div>
            </div>

            {/* Staging notice */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-5">
              <div className="flex items-start gap-2">
                <i className="ri-tools-line text-amber-600 mt-0.5 shrink-0"></i>
                <div className="text-xs text-amber-800 leading-relaxed">
                  <span className="font-semibold">目前為 staging UI。</span>
                  <br />
                  正式列印字號與 audit 待 paper print backend 完成。本次列印不產生正式 print serial、不寫入 audit、不寫 DB。
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowWarning(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line mr-1.5"></i>
                確認列印
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}